/**
 * Shared horizontal-strip pager metrics (overflow scroll + snap, not slick).
 * Step = distance between consecutive slide positions (ΔoffsetLeft catches flex gap quirks).
 */

/** Treat sub-pixel leftovers as overflow so dots still show when the strip barely scrolls. */
const DEFAULT_MIN_SCROLL_FOR_PAGER = 2;

/** @returns {{ step: number, gapFallback: number }} */
export function measureStripStride(trackEl) {
  if (!(trackEl instanceof Element) || trackEl.children.length === 0) {
    return { step: 0, gapFallback: 0 };
  }
  const c0 = trackEl.children[0];
  let step = 0;
  const c1 = trackEl.children[1];
  if (c1 instanceof Element && c0 instanceof Element) {
    step = Math.abs(c1.offsetLeft - c0.offsetLeft);
  }
  let gapFallback = 0;
  if (typeof window !== "undefined") {
    const cs = window.getComputedStyle(trackEl);
    gapFallback = parseFloat(cs.columnGap || cs.gap || "0") || 0;
  }
  if (!(step >= 8)) {
    if (c0 instanceof Element && typeof c0.getBoundingClientRect === "function") {
      step = c0.getBoundingClientRect().width + gapFallback;
    }
  }
  return { step: step >= 8 ? step : 0, gapFallback };
}

/**
 * @param {HTMLElement} scrollerEl - overflow-x element
 * @param {number} slideCount - cap for dot count (usually list length)
 */
export function readStripPagerPages(scrollerEl, slideCount, options = {}) {
  const minScroll =
    typeof options.minScrollForPager === "number"
      ? options.minScrollForPager
      : DEFAULT_MIN_SCROLL_FOR_PAGER;

  const { scrollWidth, clientWidth } = scrollerEl;
  if (!(clientWidth > 0) || slideCount < 1) {
    return { maxScroll: 0, pages: 1, step: 0 };
  }

  const maxScroll = Math.max(0, scrollWidth - clientWidth);
  /**
   * Sub-pixel / flex rounding: treat near-zero overflow as none.
   */
  if (maxScroll <= minScroll || slideCount < 2) {
    return { maxScroll, pages: 1, step: 0 };
  }

  const track = scrollerEl.firstElementChild;
  const { step: measuredStep } = measureStripStride(track);
  const step =
    measuredStep > 0
      ? measuredStep
      : maxScroll > 0
        ? maxScroll / Math.max(slideCount - 1, 1)
        : 0;

  if (!(step >= 4)) {
    const pagesGuess = Math.min(slideCount, 2);
    return { maxScroll, pages: Math.max(pagesGuess, 1), step: Math.max(step, 1) };
  }

  let rawPages = Math.floor(maxScroll / step) + 1;
  if (maxScroll > minScroll && rawPages < 2) rawPages = 2;
  const pages = Math.max(1, Math.min(slideCount, rawPages));

  return { maxScroll, pages, step };
}

export function stripScrollTargets(maxScroll, pages, step) {
  if (pages <= 1) return [0];
  const out = [];
  for (let i = 0; i < pages; i++) {
    out.push(Math.min(maxScroll, Math.round(i * step)));
  }
  return out;
}

export function activeStripPageIndex(scrollLeft, maxScroll, pages, step) {
  if (pages <= 1) return 0;
  let best = 0;
  let bestDist = Infinity;
  for (let j = 0; j < pages; j++) {
    const target = Math.min(maxScroll, Math.round(j * step));
    const d = Math.abs(scrollLeft - target);
    if (d < bestDist) {
      bestDist = d;
      best = j;
    }
  }
  return best;
}

export function stripScrollLeftForPage(pageIndex, pages, maxScroll, step) {
  if (pages <= 1) return 0;
  if (pageIndex <= 0) return 0;
  return Math.min(maxScroll, Math.round(Math.max(0, pageIndex) * step));
}

/** Snapshot anchor `offsetLeft`s for grouped pager midpoints — call on resize/layout, not every scroll. */
export function stripPagerPageAnchors(scrollerEl, slideCount, pages, groupSize) {
  if (pages < 2 || slideCount < 2) return null;
  const track = scrollerEl.firstElementChild;
  if (!track?.children?.length) return null;
  const anchors = [];
  for (let p = 0; p < pages; p += 1) {
    const si = slideIndexForPagerPage(p, pages, groupSize, slideCount);
    const ch = track.children[si];
    anchors.push(ch instanceof HTMLElement ? ch.offsetLeft : 0);
  }
  return anchors;
}

/** Pager tap → slide index aligned to grouped “pages”. */
export function slideIndexForPagerPage(pageIndex, pages, groupSize, slideCount) {
  const lastSlide = Math.max(0, slideCount - 1);
  if (slideCount <= 1) return 0;
  const pgs = Math.max(1, pages || 1);
  const pi = Math.max(0, pageIndex);
  if (pi >= pgs - 1) return lastSlide;
  const g = Math.max(1, groupSize || 1);
  return Math.min(lastSlide, pi * g);
}

/**
 * Map slide index → condensed pager index. Uses the same boundaries as `slideIndexForPagerPage`
 * (not `floor(slideIdx / groupSize)`), so the last dot activates when the final card is active
 * even when the last group is larger than `groupSize`.
 */
export function pagerIndexFromSlideIndex(slideIdx, groupSize, slideCount, pages) {
  if (pages <= 1 || slideCount < 2) return 0;
  const last = Math.max(0, slideCount - 1);
  const idx = Math.max(0, Math.min(last, Number(slideIdx) || 0));
  if (idx >= last) return pages - 1;
  for (let p = 0; p < pages - 1; p += 1) {
    const start = slideIndexForPagerPage(p, pages, groupSize, slideCount);
    const nextStart = slideIndexForPagerPage(p + 1, pages, groupSize, slideCount);
    if (idx >= start && idx < nextStart) return p;
  }
  return pages - 1;
}

/**
 * Active pager “page” from `scrollLeft` using **midpoints between page anchor positions**
 * (`offsetLeft` of the first slide in each group). Matches grouped dots when the user stops
 * between two cards that belong to different groups — `pagerIndexFromSlideIndex(nearestSlide)`
 * can flicker or disagree with scroll snap. Use with `slideIndexForPagerPage` + `scrollStripToSlideIndex`.
 *
 * Optional `options`: hysteresis (`scrollDelta`, `prevPageIndex`, …) plus **`maxScroll` / `pageAnchors`**
 * from a layout pass — when both are set, scroll handlers avoid `scrollWidth` / `clientWidth` / `offsetLeft`
 * reads (reduces forced reflow from interleaved style + layout reads).
 */
export function pagerPageIndexFromScroll(
  scrollerEl,
  scrollLeft,
  slideCount,
  pages,
  groupSize,
  endBiasPx = 16,
  options = undefined
) {
  if (pages <= 1 || slideCount < 2) return 0;
  const maxScroll =
    typeof options?.maxScroll === "number" && options.maxScroll >= 0
      ? options.maxScroll
      : Math.max(0, scrollerEl.scrollWidth - scrollerEl.clientWidth);
  const sl = Math.min(Math.max(0, scrollLeft), maxScroll);
  if (maxScroll > endBiasPx && sl >= maxScroll - endBiasPx) {
    return pages - 1;
  }
  const startClampPx =
    typeof options?.startBiasPx === "number"
      ? options.startBiasPx
      : Math.max(endBiasPx, 36);
  /** Snap + `scroll-padding` often leave small non-zero `scrollLeft` on the first slide. */
  if (maxScroll > startClampPx && sl <= startClampPx) {
    return 0;
  }
  const scrollDelta = options?.scrollDelta ?? 0;
  const prevPageIndex = options?.prevPageIndex;
  const g = Math.max(1, groupSize || 1);
  let anchors = options?.pageAnchors;
  if (!Array.isArray(anchors) || anchors.length !== pages) {
    const track = scrollerEl.firstElementChild;
    if (!track?.children?.length) return 0;
    anchors = [];
    for (let p = 0; p < pages; p += 1) {
      const si = slideIndexForPagerPage(p, pages, groupSize, slideCount);
      const ch = track.children[si];
      anchors.push(ch instanceof HTMLElement ? ch.offsetLeft : 0);
    }
  }
  for (let p = 0; p < pages - 1; p += 1) {
    const a = anchors[p];
    const b = anchors[p + 1];
    let mid = a === b ? a + 0.5 : (a + b) / 2;
    if (
      g > 1 &&
      scrollDelta < -0.25 &&
      typeof prevPageIndex === "number" &&
      prevPageIndex === p + 1
    ) {
      const span = Math.abs(b - a);
      const h =
        typeof options?.hysteresisPx === "number"
          ? options.hysteresisPx
          : span > 0
            ? Math.min(56, Math.max(14, Math.round(span * 0.2)))
            : 20;
      mid -= h;
    }
    if (sl < mid) return p;
  }
  return pages - 1;
}

/** Scroll a horizontal strip to the start of a slide (works with snap + scroll-padding). */
export function scrollStripToSlideIndex(scrollerEl, slideIdx) {
  const track = scrollerEl.firstElementChild;
  const child = track?.children?.[slideIdx];
  if (child instanceof HTMLElement) {
    child.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
  }
}

/**
 * Closest snap column by track `offsetLeft`. When scrolled flush to the end, always treat as
 * the last slide so the final pager dot matches the last card (sub-pixel / snap rounding).
 *
 * `scrollLeft` is clamped to `[0, maxScroll]` so rubber-band / overscroll values do not snap
 * the pager to slide 0 while the user is still mid-strip (common on iOS).
 */
export function nearestStripSlideIndexFromScroll(
  scrollerEl,
  scrollLeft,
  slideCount,
  endBiasPx = 12
) {
  const track = scrollerEl.firstElementChild;
  if (!track?.children?.length || slideCount < 1) return 0;
  const last = Math.max(0, slideCount - 1);
  const maxScroll = Math.max(0, scrollerEl.scrollWidth - scrollerEl.clientWidth);
  const sl = Math.min(Math.max(0, scrollLeft), maxScroll);
  if (maxScroll > endBiasPx && sl >= maxScroll - endBiasPx) {
    return last;
  }
  let best = 0;
  let bestDist = Infinity;
  const n = Math.min(track.children.length, slideCount);
  for (let i = 0; i < n; i++) {
    const c = track.children[i];
    if (!(c instanceof HTMLElement)) continue;
    const d = Math.abs(sl - c.offsetLeft);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return Math.min(last, best);
}

/**
 * Same as `nearestStripSlideIndexFromScroll` end handling, but slide `i` is active while
 * `scrollLeft` lies in `[mid(i-1,i), mid(i,i+1))` where `mid(a,b)=(offset[a]+offset[b])/2`.
 * At the exact midpoint between two cards this picks the **later** slide — avoids the
 * “nearest left edge” tie that favors slide 0, so the first pager dot does not light up
 * early on wide one-card-at-a-time strips (e.g. homepage reviews).
 */
export function nearestStripSlideIndexFromScrollMidpoints(
  scrollerEl,
  scrollLeft,
  slideCount,
  endBiasPx = 16
) {
  const track = scrollerEl.firstElementChild;
  if (!track?.children?.length || slideCount < 1) return 0;
  const last = Math.max(0, slideCount - 1);
  const maxScroll = Math.max(0, scrollerEl.scrollWidth - scrollerEl.clientWidth);
  const sl = Math.min(Math.max(0, scrollLeft), maxScroll);
  if (maxScroll > endBiasPx && sl >= maxScroll - endBiasPx) {
    return last;
  }
  const n = Math.min(track.children.length, slideCount);
  const offsets = [];
  for (let i = 0; i < n; i++) {
    const c = track.children[i];
    if (c instanceof HTMLElement) offsets.push(c.offsetLeft);
  }
  if (offsets.length < 2) return 0;
  for (let i = 0; i < offsets.length - 1; i += 1) {
    const mid = (offsets[i] + offsets[i + 1]) / 2;
    if (sl < mid) return Math.min(last, i);
  }
  return last;
}

/**
 * New Arrivals style: fewer dots (~2–many cards between), hard cap dots per breakpoint.
 */
export function computeGroupedStripPager(scrollerEl, slideCount, opts = {}) {
  const maxDotsMobile = opts.maxDotsMobile ?? 8;
  const maxDotsDesktop = opts.maxDotsDesktop ?? 12;
  const minScrollPx = opts.minScrollPx ?? 4;

  /** Default phone-only; pass e.g. `(max-width: 1023px)` for strip carousels on tablet too. */
  const compactMq = opts.compactStripMq ?? "(max-width: 639px)";
  const narrow =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(compactMq).matches;
  const maxDots = narrow ? maxDotsMobile : maxDotsDesktop;
  /** Lower = tighter pager (fewer slides under “dot 1”) — pass `1` for one-card-at-a-time strips. */
  const minGroupFloor = narrow
    ? (typeof opts.minGroupCompact === "number" ? opts.minGroupCompact : 3)
    : typeof opts.minGroupWide === "number"
      ? opts.minGroupWide
      : 2;

  const cw = scrollerEl.clientWidth;
  const maxScroll = Math.max(0, scrollerEl.scrollWidth - cw);
  const track = scrollerEl.firstElementChild;

  if (slideCount < 2 || maxScroll < minScrollPx) {
    return { pages: 1, groupSize: 1, stride: 0, maxScroll };
  }

  let { step: stride } = measureStripStride(track);
  if (!(stride >= 8)) {
    stride = maxScroll / Math.max(slideCount - 1, 1);
  }
  const stepForGroup = Math.max(8, stride || 1);

  /** Fixed slides per pager “page” (e.g. New Arrivals: 2 cards per dot). Still respects `maxDots*`. */
  if (typeof opts.fixedGroupSize === "number" && opts.fixedGroupSize >= 1) {
    let groupSize = Math.min(Math.max(1, Math.floor(opts.fixedGroupSize)), slideCount);
    let pages = Math.ceil(slideCount / groupSize);
    while (pages > maxDots && groupSize < slideCount) {
      groupSize += 1;
      pages = Math.ceil(slideCount / groupSize);
    }
    return { pages, groupSize, stride: stepForGroup, maxScroll };
  }

  const approxRaw = Math.max(1, Math.floor(cw / stepForGroup));
  /** Wide `clientWidth` inflates grouping → far fewer pager dots than on phones; cap for parity. */
  const approxCap = narrow ? 6 : 3;
  const approxVisible = Math.min(approxRaw, approxCap);
  let groupSize = Math.max(minGroupFloor, approxVisible);

  groupSize = Math.max(groupSize, Math.ceil(slideCount / maxDots));

  let pages = Math.ceil(slideCount / groupSize);
  while (pages > maxDots && groupSize < slideCount) {
    groupSize += 1;
    pages = Math.ceil(slideCount / groupSize);
  }
  return { pages, groupSize, stride: stepForGroup, maxScroll };
}
